import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import { useNotificationsStore } from './stores/notificationsStore'
import { targetPath } from './targets'

const TARGET_LABELS = {
    PERSON: 'Open contact',
    LEAD: 'Open lead',
    OPPORTUNITY: 'Open on pipeline',
    DEAL: 'Open quote',
    LISTING: 'Open listing',
}

const formatFull = (iso) =>
    iso ? new Date(iso).toLocaleString(undefined, {
        dateStyle: 'medium', timeStyle: 'short',
    }) : '—'

export const NotificationDetail = ({ notification }) => {
    const navigate = useNavigate()
    const clearSelection = useNotificationsStore((s) => s.clearSelection)
    const archive = useNotificationsStore((s) => s.archive)
    const unarchive = useNotificationsStore((s) => s.unarchive)

    // On mobile this pane replaces the list, so opening a row near the bottom
    // would otherwise render the detail already scrolled past its own heading.
    const notificationId = notification?.id
    useEffect(() => {
        if (notificationId && window.matchMedia('(max-width: 900px)').matches) {
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }, [notificationId])

    if (!notification) {
        return (
            <aside className="notif-detail empty">
                <p>Select a notification to read it.</p>
            </aside>
        )
    }

    const path = targetPath(notification.target)

    const onArchive = () => {
        archive(notification.id)
        // The row is leaving the feed, so the pane must stop showing it.
        clearSelection()
    }

    return (
        <aside className="notif-detail">
            <header className="notif-detail-head">
                {/* Back on mobile (where this pane replaces the list), close on
                  * desktop (where it sits beside it). Both call the same thing;
                  * only the affordance differs, so it's a CSS swap rather than a
                  * viewport hook that would re-render the pane on every resize. */}
                <button
                    className="notif-detail-back"
                    onClick={clearSelection}
                    aria-label="Back to notifications"
                >
                    <ArrowBackIcon fontSize="small" />
                    <span>All notifications</span>
                </button>

                <span className="notif-detail-source">{notification.source}</span>

                <button className="notif-detail-close" onClick={clearSelection} aria-label="Close">
                    <CloseIcon fontSize="small" />
                </button>
            </header>

            <h2 className="notif-detail-title">{notification.title}</h2>
            {notification.body && <p className="notif-detail-body">{notification.body}</p>}

            <dl className="notif-detail-meta">
                <div><dt>Received</dt><dd>{formatFull(notification.created_at)}</dd></div>
                <div><dt>Status</dt><dd>{notification.status}</dd></div>
                {notification.read_at && (
                    <div><dt>Read</dt><dd>{formatFull(notification.read_at)}</dd></div>
                )}
            </dl>

            <div className="notif-detail-actions">
                {path && (
                    <button className="notif-detail-open" onClick={() => navigate(path)}>
                        <OpenInNewIcon style={{ fontSize: 16 }} />
                        {TARGET_LABELS[notification.target.type] || 'Open'}
                    </button>
                )}
                {notification.status === 'ARCHIVED' ? (
                    <button
                        className="notif-detail-archive"
                        onClick={() => unarchive(notification.id)}
                    >
                        <UnarchiveOutlinedIcon style={{ fontSize: 16 }} /> Unarchive
                    </button>
                ) : (
                    <button className="notif-detail-archive" onClick={onArchive}>
                        <ArchiveOutlinedIcon style={{ fontSize: 16 }} /> Archive
                    </button>
                )}
            </div>

            {!path && (
                <p className="notif-detail-note">
                    This notification isn’t linked to a record.
                </p>
            )}
        </aside>
    )
}
